// ProductList.jsx
// מציג קטגוריות ראשיות ופותח רשימת תתי־קטגוריות נגישה

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Popover,
  Stack,
  Typography,
} from "@mui/material";

import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { GetCategories } from "../utils/categoryAPI";
import { setCategries } from "../features/categoriesSlice";

/**
 * תמיכה בשני השמות האפשריים של מזהה קטגוריית האב.
 */
const getParentId = (category) =>
  category?.parentCategoryId ??
  category?.parentCategoryID ??
  null;

export default function ProductList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [catLoading, setCatLoading] = useState(true);
  const [catError, setCatError] = useState("");

  const [anchorEl, setAnchorEl] = useState(null);
  const [openRootId, setOpenRootId] = useState(null);

  /*
   * נשמור הפניה לכפתור שפתח את החלונית,
   * כדי להחזיר אליו את הפוקוס לאחר הסגירה.
   */
  const openerButtonRef = useRef(null);

  const popoverOpen = Boolean(anchorEl);

  useEffect(() => {
    let mounted = true;

    const loadCategories = async () => {
      setCatLoading(true);
      setCatError("");

      try {
        const data = await GetCategories();

        if (!mounted) {
          return;
        }

        const categoriesArray = Array.isArray(data)
          ? data
          : [];

        setCategories(categoriesArray);
        dispatch(setCategries(categoriesArray));
      } catch (error) {
        console.error("שגיאה בטעינת קטגוריות:", error);

        if (mounted) {
          setCatError(
            "לא הצלחנו לטעון את הקטגוריות. נסו שוב מאוחר יותר."
          );
        }
      } finally {
        if (mounted) {
          setCatLoading(false);
        }
      }
    };

    loadCategories();

    return () => {
      mounted = false;
    };
  }, [dispatch]);

  /**
   * קטגוריות ראשיות בלבד.
   */
  const rootCategories = useMemo(() => {
    return categories.filter(
      (category) => !getParentId(category)
    );
  }, [categories]);

  /**
   * תתי־הקטגוריות של הקטגוריה הראשית שנפתחה.
   */
  const subCategories = useMemo(() => {
    if (openRootId === null) {
      return [];
    }

    return categories.filter(
      (category) =>
        getParentId(category) === openRootId
    );
  }, [categories, openRootId]);

  const selectedRootCategory = useMemo(() => {
    return rootCategories.find(
      (category) => category.id === openRootId
    );
  }, [rootCategories, openRootId]);

  const handleOpenRoot = (event, rootId) => {
    /*
     * אם לחצו שוב על אותה קטגוריה כשהחלונית פתוחה,
     * נסגור אותה.
     */
    if (
      popoverOpen &&
      openRootId === rootId
    ) {
      handleClosePopover();
      return;
    }

    openerButtonRef.current =
      event.currentTarget;

    setAnchorEl(event.currentTarget);
    setOpenRootId(rootId);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
    setOpenRootId(null);

    /*
     * החזרת הפוקוס לכפתור שפתח את החלונית.
     */
    window.requestAnimationFrame(() => {
      openerButtonRef.current?.focus();
    });
  };

  const handleSelectSubCategory = (subId) => {
    setAnchorEl(null);
    setOpenRootId(null);

    navigate(`/products/sub/${subId}`);
  };

  const handleRootKeyDown = (
    event,
    rootId
  ) => {
    /*
     * Enter ורווח מטופלים כברירת מחדל על ידי Button.
     * החצים פותחים את תפריט הקטגוריה.
     */
    if (
      event.key === "ArrowDown" ||
      event.key === "ArrowUp"
    ) {
      event.preventDefault();
      handleOpenRoot(event, rootId);
    }

    if (
      event.key === "Escape" &&
      popoverOpen
    ) {
      event.preventDefault();
      handleClosePopover();
    }
  };

  const handleSubCategoryKeyDown = (
    event,
    subId
  ) => {
    if (event.key === "Escape") {
      event.preventDefault();
      handleClosePopover();
      return;
    }

    if (
      event.key === "Enter" ||
      event.key === " "
    ) {
      event.preventDefault();
      handleSelectSubCategory(subId);
    }
  };

  return (
    <Box
      component="section"
      aria-label="קטגוריות המוצרים"
      dir="rtl"
      sx={{
        width: "100%",
        p: {
          xs: 1,
          md: 2,
        },

        /*
         * מסגרת ברורה לכל רכיב שנבחר באמצעות Tab.
         */
        "& button:focus-visible, & a:focus-visible":
          {
            outline:
              "3px solid #000000",
            outlineOffset: "3px",
            borderRadius: "5px",
          },
      }}
    >
      {catLoading ? (
        <Stack
          role="status"
          aria-live="polite"
          alignItems="center"
          justifyContent="center"
          spacing={2}
          py={4}
        >
          <CircularProgress
            aria-hidden="true"
          />

          <Typography component="span">
            הקטגוריות נטענות
          </Typography>
        </Stack>
      ) : catError ? (
        <Alert
          severity="error"
          role="alert"
          sx={{
            my: 2,
          }}
        >
          {catError}
        </Alert>
      ) : (
        <Box
          component="nav"
          aria-label="קטגוריות ראשיות"
          dir="rtl"
          sx={{
            display: "block",
            width: "100%",
            overflowX: "auto",
            overflowY: "visible",
            whiteSpace: "nowrap",
            mb: 2,
            pb: 1,

            /*
             * משאירים את פס הגלילה זמין.
             * הסתרה מלאה של פס גלילה עלולה להקשות
             * על משתמשים להבין שיש תוכן נוסף.
             */
            scrollbarWidth: "thin",

            "&::-webkit-scrollbar": {
              height: 7,
            },

            "&::-webkit-scrollbar-thumb":
              {
                backgroundColor:
                  "rgba(0, 0, 0, 0.35)",
                borderRadius: 4,
              },
          }}
        >
          {rootCategories.map(
            (category) => {
              const isOpen =
                popoverOpen &&
                openRootId === category.id;

              const buttonId = `root-category-button-${category.id}`;
              const popoverId = `root-category-popover-${category.id}`;

              return (
                <Button
                  id={buttonId}
                  key={category.id}
                  type="button"
                  variant="text"
                  size="medium"
                  onClick={(event) =>
                    handleOpenRoot(
                      event,
                      category.id
                    )
                  }
                  onKeyDown={(event) =>
                    handleRootKeyDown(
                      event,
                      category.id
                    )
                  }
                  aria-haspopup="menu"
                  aria-expanded={isOpen}
                  aria-controls={
                    isOpen
                      ? popoverId
                      : undefined
                  }
                  aria-label={`פתיחת קטגוריית ${category.nameCategor}`}
                  sx={{
                    mx: 0.75,
                    py: 0.75,
                    px: 1,
                    mt: 1.5,
                    minWidth: "auto",
                    whiteSpace: "nowrap",
                    fontSize: {
                      xs: 18,
                      sm: 30,
                    },
                    fontWeight: 900,
                    textTransform: "none",
                    letterSpacing:
                      "0.2px",
                    lineHeight: 1.1,
                    fontFamily:
                      '"Rubik", "Heebo", "Assistant", sans-serif',
                    color: "#D81B60",
                    borderRadius: "5px",

                    "&:hover": {
                      backgroundColor:
                        "rgba(216, 27, 96, 0.08)",
                      textDecoration:
                        "underline",
                    },

                    "&:focus-visible": {
                      outline:
                        "3px solid #000000",
                      outlineOffset: "3px",
                      backgroundColor:
                        "rgba(216, 27, 96, 0.12)",
                    },

                    /*
                     * סימון הקטגוריה שהחלונית שלה פתוחה.
                     */
                    ...(isOpen && {
                      backgroundColor:
                        "rgba(216, 27, 96, 0.12)",
                      textDecoration:
                        "underline",
                    }),
                  }}
                >
                  {category.nameCategor}
                </Button>
              );
            }
          )}

          {rootCategories.length === 0 && (
            <Typography
              component="p"
              color="text.secondary"
              sx={{
                py: 2,
                textAlign: "center",
              }}
            >
              אין קטגוריות להצגה
            </Typography>
          )}
        </Box>
      )}

      <Popover
        id={
          openRootId !== null
            ? `root-category-popover-${openRootId}`
            : undefined
        }
        open={popoverOpen}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        disableRestoreFocus
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            role: "menu",
            "aria-label":
              selectedRootCategory?.nameCategor
                ? `תתי־קטגוריות של ${selectedRootCategory.nameCategor}`
                : "תתי־קטגוריות",
            sx: {
              p: 2,
              borderRadius: 2,
              minWidth: {
                xs: "min(360px, calc(100vw - 24px))",
                md: 640,
              },
              maxWidth:
                "calc(100vw - 24px)",
              maxHeight:
                "calc(100vh - 120px)",
              overflowY: "auto",
              overflowX: "hidden",
              boxShadow: 6,
              direction: "rtl",

              "& button:focus-visible":
                {
                  outline:
                    "3px solid #000000",
                  outlineOffset: "2px",
                  borderRadius: "4px",
                },
            },
          },
        }}
      >
        <Box
          dir="rtl"
          sx={{
            columnCount: {
              xs: 1,
              sm: 2,
            },
            columnGap: 2,
            py: 0.5,
          }}
        >
          {subCategories.length > 0 ? (
            subCategories.map(
              (subCategory) => (
                <Button
                  key={subCategory.id}
                  type="button"
                  role="menuitem"
                  onClick={() =>
                    handleSelectSubCategory(
                      subCategory.id
                    )
                  }
                  onKeyDown={(event) =>
                    handleSubCategoryKeyDown(
                      event,
                      subCategory.id
                    )
                  }
                  sx={{
                    display: "block",
                    width: "100%",
                    breakInside: "avoid",
                    color: "#D81B60",
                    textAlign: "right",
                    justifyContent:
                      "flex-start",
                    fontFamily:
                      '"Rubik", "Heebo", "Assistant", sans-serif',
                    fontSize: {
                      xs: 16,
                      md: 18,
                    },
                    fontWeight: 700,
                    lineHeight: 1.5,
                    py: 0.75,
                    px: 1,
                    mb: 0.25,
                    textTransform: "none",
                    whiteSpace: "normal",
                    borderRadius: "4px",

                    "&:hover": {
                      textDecoration:
                        "underline",
                      backgroundColor:
                        "rgba(216, 27, 96, 0.08)",
                    },

                    "&:focus-visible": {
                      outline:
                        "3px solid #000000",
                      outlineOffset: "2px",
                      backgroundColor:
                        "rgba(216, 27, 96, 0.12)",
                      textDecoration:
                        "underline",
                    },
                  }}
                >
                  {subCategory.nameCategor}
                </Button>
              )
            )
          ) : (
            <Typography
              component="p"
              role="status"
              color="text.secondary"
              sx={{
                py: 1,
              }}
            >
              אין תתי־קטגוריות לקטגוריה זו
            </Typography>
          )}
        </Box>
      </Popover>
    </Box>
  );
}